import {get, isEmpty, isNil} from 'lodash'
import {
  DeliveryMethodType,
  DeliveryNoteStatus,
  getDefaultDeliveryMethodId,
  getDeliveryMethod,
} from 'helpers/deliveries/index'
import {getInvoiceFinancialYearStart} from 'selectors/settings'
import {getDeliveryNotes} from 'selectors/deliveries'
import {getDateFilters} from 'helpers/dateRange'
import theme from 'theme/index'
import {getLocation, LocationTypes} from 'helpers/locations'
import {getContactName} from 'helpers/contacts'
import {
  getJobCardCreateType,
  isOrderProductionPending,
  JobBoardStatus,
  JobCardCreateType,
} from 'helpers/production'
import {isPrintTemplatePublished} from 'helpers/printTemplates'
import {PrintTemplateType} from 'helpers/printTemplates/enum'
import {getSearchTerm} from "helpers/string"
import {getSearch} from "selectors/pivotTable"
import {deliveryNotesPivotTableKey} from "reducers/deliveriesContext/deliveryNotes"

export const getDeliveryNoteStatusName = (deliveryNote, deliveryMethods, isNew = false) => {
  const status = get(deliveryNote, 'status')
  const isReturnNote = get(deliveryNote, 'returnNote', false)

  if (isNew) return 'Draft'

  if (isReturnNote) {
    if (status === DeliveryNoteStatus.SCHEDULED)
      return 'Awaiting Return'

    if (status === DeliveryNoteStatus.DELIVERED)
      return 'Returned'
  }

  if (status === DeliveryNoteStatus.SCHEDULED)
    return 'Scheduled'

  if (status === DeliveryNoteStatus.DELIVERED) {
    const deliveryMethod = getDeliveryMethod(deliveryNote.deliveryMethodId, deliveryMethods)
    return !isNil(deliveryMethod) && deliveryMethod.deliveryMethodType === DeliveryMethodType.COLLECTION
        ? 'Collected'
        : 'Delivered'
  }

  return null
}

export const getColorForDeliveryNoteStatus = (deliveryNote, isNew = false) => {
  const status = get(deliveryNote, 'status')
  const isReturnNote = get(deliveryNote, 'returnNote', false)

  if (isReturnNote) {
    if (isNew)
      return theme.palette.deliveryNoteStatus.draftReturn

    if (status === DeliveryNoteStatus.SCHEDULED)
      return theme.palette.deliveryNoteStatus.awaitingReturn

    if (status === DeliveryNoteStatus.DELIVERED)
      return theme.palette.deliveryNoteStatus.returned
  }

  if (isNew)
    return theme.palette.deliveryNoteStatus.draft

  if (status === DeliveryNoteStatus.SCHEDULED)
    return theme.palette.deliveryNoteStatus.scheduled

  if (status === DeliveryNoteStatus.DELIVERED)
    return theme.palette.deliveryNoteStatus.delivered

  return null
}

export const getColorForDeliveryNoteStatusOptions = option => {
  switch (option) {
    case 'AWAITING_RETURN':
      return theme.palette.deliveryNoteStatus.awaitingReturn
    case 'RETURNED':
      return theme.palette.deliveryNoteStatus.returned
    case DeliveryNoteStatus.SCHEDULED:
      return theme.palette.deliveryNoteStatus.scheduled
    case DeliveryNoteStatus.DELIVERED:
      return theme.palette.deliveryNoteStatus.delivered
    case 'COLLECTED':
      return theme.palette.deliveryNoteStatus.collected
    default:
      return null
  }
}

export const getDeliveryNoteParams = (state) => {
  const invoiceFinancialYearStart = getInvoiceFinancialYearStart(state)
  const searchTerm = getSearch(state, deliveryNotesPivotTableKey)
  const filters = getDeliveryNotes(state)
  const scheduledDateFilters = getDateFilters({
    timeKey: filters.scheduledDateTimeKey,
    targetStart: filters.scheduledDateTargetStart,
    targetEnd: filters.scheduledDateTargetEnd,
  }, invoiceFinancialYearStart)

  const deliveredDateFilters = getDateFilters({
    timeKey: filters.deliveredDateTimeKey,
    targetStart: filters.deliveredDateTargetStart,
    targetEnd: filters.deliveredDateTargetEnd,
  }, invoiceFinancialYearStart)

  const {
    status,
    jobStatus,
    printedEmailed,
    deliveryMethod,
    returnReason,
  } = filters

  const params = {}

  if (!isNil(scheduledDateFilters.targetStart) && !isNil(scheduledDateFilters.targetEnd)) {
    params['scheduledDateTargetStart'] = scheduledDateFilters.targetStart
    params['scheduledDateTargetEnd'] = scheduledDateFilters.targetEnd
  }

  if (!isNil(deliveredDateFilters.targetStart) && !isNil(deliveredDateFilters.targetEnd)) {
    params['deliveredDateTargetStart'] = deliveredDateFilters.targetStart
    params['deliveredDateTargetEnd'] = deliveredDateFilters.targetEnd
  }

  if (!isEmpty(status))
    params['status'] = status.map(option => option.toString())

  if (!isEmpty(jobStatus))
    params['jobStatus'] = jobStatus

  if (!isEmpty(printedEmailed))
    params['printedEmailed'] = printedEmailed

  if (!isEmpty(deliveryMethod))
    params['deliveryMethods'] = deliveryMethod

  if (!isEmpty(returnReason))
    params['returnReasons'] = returnReason

  if (getSearchTerm(searchTerm))
    params['searchTerm'] = searchTerm

  return params
}

export const getDeliveryNoteAddress = (deliveryNote) => {
  const {address1, address2, address3} = deliveryNote
  const addressParts = []
  if (!isEmpty(address1)) addressParts.push(address1)
  if (!isEmpty(address2)) addressParts.push(address2)
  if (!isEmpty(address3)) addressParts.push(address3)
  return addressParts.join('\r')
}

export const getDefaultDeliveryNoteLocations = (order, returnNote = false, homeCompany) => {
  return returnNote
      ? get(homeCompany, 'locations', [])
      : get(order, 'company.locations', [])
}

export const getDefaultDeliveryNoteAddress = (order, returnNote = false, homeCompany) => {
  if (returnNote) return getReturnNoteAddress(homeCompany)

  const orderDeliveryAddress = get(order, 'overrides.deliveryAddress')
  const companyDeliveryAddress = getCompanyDeliveryAddress(order)
  const orderAddress = getOrderAddress(order)

  return isAddressValid(orderDeliveryAddress)
      ? orderDeliveryAddress
      : isAddressValid(companyDeliveryAddress)
          ? companyDeliveryAddress
          : orderAddress
}

export const getReturnNoteAddress = (homeCompany) => {
  const mainContact = homeCompany.maincontact[0]
  const locations = get(homeCompany, 'locations', []) || []
  const mainAddress = getLocation(locations, LocationTypes.main)
  const deliveryAddress = getLocation(locations, LocationTypes.delivery)
  const address = isNil(deliveryAddress) ? mainAddress : deliveryAddress

  return {
    company: get(homeCompany, 'name'),
    contact: getContactName(mainContact),
    address1: address.address1,
    address2: address.address2,
    address3: address.address3,
    city: address.city,
    county: address.county,
    postCode: address.postCode,
    country: address.country,
    phone: address.phone,
  }
}

const getCompanyDeliveryAddress = (order) => {
  const locations = get(order, 'company.locations', [])
  const deliveryAddress = getLocation(locations, LocationTypes.delivery)

  return isNil(deliveryAddress)
      ? null
      : {
        company: order.company.name,
        contact: getContactName(order.contact),
        address1: deliveryAddress.address1,
        address2: deliveryAddress.address2,
        address3: deliveryAddress.address3,
        city: deliveryAddress.city,
        county: deliveryAddress.county,
        postCode: deliveryAddress.postCode,
        country: deliveryAddress.country,
        phone: deliveryAddress.phone,
      }
}

const getOrderAddress = (order) => {
  const contactAddress = get(order, 'contact.location', {})
  const {address1, address2, address3, city, county, postCode, country} = contactAddress || {}
  return {
    company: order.company.name,
    contact: getContactName(order.contact),
    address1,
    address2,
    address3,
    city,
    county,
    postCode,
    country,
    phone: isEmpty(order.contact.phone) ? order.contact.mobile : order.contact.phone,
  }
}

const isAddressValid = (address) =>
    !isNil(address) && (
        !isEmpty(address.company) ||
        !isEmpty(address.contact) ||
        !isEmpty(address.address1) ||
        !isEmpty(address.address2) ||
        !isEmpty(address.address3) ||
        !isEmpty(address.city) ||
        !isEmpty(address.county) ||
        !isEmpty(address.postCode) ||
        !isEmpty(address.country) ||
        !isEmpty(address.phone)
    )

export const createNewDeliveryNote = (orderId, returnNote, deliveryMethods) => {
  return {
    orderId,
    deliveryMethodId: getDefaultDeliveryMethodId(deliveryMethods),
    status: DeliveryNoteStatus.SCHEDULED,
    returnNote,
    printOptions: {
      deliveryNoteId: -1,
      printEoriNumberEU: false,
      printEoriNumberNI: false,
      printPackTypes: true,
      createdDate: new Date().getTime(),
      modifiedDate: new Date().getTime(),
    },
  }
}

export const hasMissingOrderItems = (orderItems, deliveryNoteItems) => {
  return getMissingOrderItems(orderItems, deliveryNoteItems).length > 0
}

export const getMissingOrderItems = (orderItems, deliveryNoteItems) => {
  if (isNil(orderItems)) return []
  return orderItems
      .filter(orderItem => !orderItem.dormant)
      .filter(orderItem => {
        return !deliveryNoteItems.some(item => item.orderItemId === orderItem.id)
      })
}

export const getItemQuantity = (deliveryNote, key) => {
  const items = get(deliveryNote, key, [])
  return isNil(items)
      ? 0
      : items.reduce((total, item) => total + item.quantity, 0)
}

export const getDeliveryNoteItemsHaveQuantity = (deliveryNoteItems) => {
  const hasQuantity = (deliveryNoteItem) => deliveryNoteItem.quantity > 0
  return deliveryNoteItems.some(hasQuantity)
}

export const getScheduleTypeWording = (isReturnNote) => {
  return isReturnNote ? 'return' : 'deliver'
}

export const getScheduleNotReadyMessage = (deliveryNoteItems, isReturnNote) => {
  const anyItemHasQuantity = getDeliveryNoteItemsHaveQuantity(deliveryNoteItems)

  if (!anyItemHasQuantity) {
    const scheduleTypeWording = getScheduleTypeWording(isReturnNote)
    return `Select a quantity of items to ${scheduleTypeWording}.`
  }

  return `Specify a schedule date to schedule this ${isReturnNote ? 'return' : 'delivery'}`
}

export const getDeliveringNotReadyMessage = (deliveryNoteItems, order, isReturnNote) => {
  const anyItemHasQuantity = getDeliveryNoteItemsHaveQuantity(deliveryNoteItems)

  if (!anyItemHasQuantity) {
    const scheduleTypeWording = getScheduleTypeWording(isReturnNote)
    return `Select a quantity of items to ${scheduleTypeWording}.`
  }

  const scheduleDateSuffix = 'You can still schedule a delivery note by setting a scheduled date.'

  return isOrderProductionPending(order)
      ? `You cannot deliver this job as it is pending on the job board. ${scheduleDateSuffix}`
      : getJobCardCreateType(order) === JobCardCreateType.LINE_ITEM
          ? `You cannot deliver this job as some line items are in progress on the job board. ${scheduleDateSuffix}`
          : `You cannot deliver this job as it is in progress on the job board. ${scheduleDateSuffix}`
}

export const getOrdersCanMarkAsDelivered = (orders) => {
  return orders.filter(canMarkOrderAsDelivered)
}

export const getOrdersCannotMarkAsDelivered = (orders) => {
  return orders.filter(order => !canMarkOrderAsDelivered(order))
}

export const canMarkOrderAsDelivered = (order) => {
  return order.jobStatus === JobBoardStatus.complete ||
      order.jobStatus === JobBoardStatus.noProductionRequired
}

export const getPrintTemplatesForDeliveryNote = (printTemplates, deliveryNote) => {
  if (isNil(printTemplates)) {
    throw new Error('Invalid argument')
  } else {

    const isReturnNote = get(deliveryNote, 'returnNote', false)
    const printTemplateType = isReturnNote
        ? PrintTemplateType.RETURN_NOTE
        : PrintTemplateType.DELIVERY_NOTE

    return printTemplates
        .filter(isPrintTemplatePublished)
        .filter(a => a.type === printTemplateType)
  }
}

export const getDeliveryNoteOption = (deliveryNote) => {
  if (isNil(deliveryNote)) return null

  return {
    label: getDeliveryNoteOptionLabel(deliveryNote),
    id: deliveryNote.id,
    value: deliveryNote.id,
    key: deliveryNote.id,
    deliveryNote,
  }
}

export const getDeliveryNoteOptionLabel = (deliveryNote) => {
  if (isNil(deliveryNote.order) || isNil(deliveryNote.deliveryItems))
    return deliveryNote.reference

  const orderItems = get(deliveryNote, 'order.orderitems', [])
  const deliveryNoteItems = get(deliveryNote, 'deliveryNoteItems', [])

  const orderItemsQuantity = orderItems.reduce((total, item) => total + item.quantity, 0)
  const deliveryNoteItemQuantity = deliveryNoteItems.reduce((total, item) => total + item.quantity, 0)

  return `${deliveryNote.reference} (${deliveryNoteItemQuantity} of ${orderItemsQuantity})`
}
